import React, { FC } from 'react';

import NextImage from 'next/image';
import { Box, Grid, GridCol, Group, MantineColor, Paper, Stack, Text, ThemeIcon } from '@mantine/core';
import { Section } from '@/../marketing/components/section/Section';
import { TbHammer, TbPencil, TbSearch } from 'react-icons/tb';

import { Gradient } from '@/../marketing/helpers/gradient';

import cx from './PageHeader.module.css';

const features = [
    { icon: <TbSearch strokeWidth={1.75} width={16} />, label: 'Discover' },
    { icon: <TbPencil strokeWidth={1.75} width={16} />, label: 'Design' },
    { icon: <TbHammer strokeWidth={1.75} width={16} />, label: 'Deploy' },
];

const PageHeader: FC<{
    title: React.ReactNode;
    titleGradient?: Gradient;
    subtitle?: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    actionsTagline?: React.ReactNode;
    image?: string;
    video?: string;
    children?: React.ReactNode;
    suffix?: React.ReactNode;
    align?: 'center' | 'left';
    color?: MantineColor;
    bg?: MantineColor;
    variant?: 'default' | 'light' | 'dark';
    bgImage?: string;
    withSection?: boolean;
}> = ({
    title,
    subtitle,
    description,
    actions,
    actionsTagline,
    image,
    video,
    suffix,
    align = 'center',
    color,
    bg,
    bgImage,
    variant,
    withSection = true,
    children,
}) => {
    const simpleHeader = (
        <SimplePageHeader
            title={title}
            subtitle={subtitle}
            description={description}
            actions={actions}
            actionsTagline={actionsTagline}
            suffix={suffix}
            align={align}
            color={color}
        />
    );

    return !image && !video && !children ? (
        <Box
            className={cx.wrapper}
            data-has-image={!!bgImage}
            style={bgImage ? { backgroundImage: `url(${bgImage})` } : undefined}
        >
            {withSection ? (
                <Section bg={bg} variant={variant}>
                    {simpleHeader}
                </Section>
            ) : (
                simpleHeader
            )}
        </Box>
    ) : (
        <Box
            className={cx.wrapper}
            data-has-image={!!bgImage}
            style={bgImage ? { backgroundImage: `url(${bgImage})` } : undefined}
        >
            <Section bg={bg} variant={variant}>
                <Grid gap={40} align="center">
                    <GridCol span={{ base: 12, md: 7 }}>
                        <Stack gap="xl">
                            {simpleHeader}

                            <Group mt={30} gap="xl" display="none">
                                {features.map((feature, index) => (
                                    <Group key={index} gap="xs">
                                        <ThemeIcon size="md" variant="light" color="blue">
                                            {feature.icon}
                                        </ThemeIcon>
                                        <Text size="sm" c="dimmed">
                                            {feature.label}
                                        </Text>
                                    </Group>
                                ))}
                            </Group>
                        </Stack>
                    </GridCol>

                    <GridCol span={{ base: 12, md: 5 }}>
                        {(image || video) && (
                            <Paper mih={320} radius="md" shadow="md">
                                {image && (
                                    <NextImage
                                        src={image}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: 'var(--mantine-radius-md)',
                                        }}
                                        role="presentation"
                                        alt=""
                                    />
                                )}
                                {video && (
                                    <video
                                        muted
                                        autoPlay
                                        loop
                                        playsInline
                                        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
                                        data-video-transition-source=""
                                    >
                                        <source src={video} />
                                    </video>
                                )}
                            </Paper>
                        )}
                        {children}
                    </GridCol>
                </Grid>
            </Section>
        </Box>
    );
};

const SimplePageHeader: FC<{
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    actionsTagline?: React.ReactNode;
    suffix?: React.ReactNode;
    align?: 'center' | 'left';
    color?: MantineColor;
}> = ({ title, subtitle, description, actions, actionsTagline, suffix, align = 'center', color = 'black' }) => (
    <Stack gap="xs" align={align === 'left' ? 'start' : 'center'}>
        <h1 className={cx.titleWrapper} style={{ textAlign: align }}>
            <span className={cx.title} style={{ color }}>
                {title}
            </span>
            <br />
            <span className={cx.subtitle} style={{ color: 'white' }}>
                {subtitle}
            </span>
        </h1>

        {description && (
            <Text
                size="xl"
                c={color === 'white' ? 'white' : 'dimmed'}
                fw={600}
                maw={600}
                ta={align}
                style={{ textWrap: 'balance' }}
            >
                {description}
            </Text>
        )}

        {actions && (
            <Box mt="xl">
                <Group>{actions}</Group>
                {actionsTagline && (
                    <Box mt="xs" c="dimmed" fz="sm">
                        {actionsTagline}
                    </Box>
                )}
            </Box>
        )}

        {suffix && <Box mt="xl">{suffix}</Box>}
    </Stack>
);

export { PageHeader };
